import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, HardDriveUpload, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { uploadFile } from "@/lib/upload";
import BulkImageTool from "@/components/admin/BulkImageTool";

const FEATURED_LIMIT = 10;
const MAX_IMAGES = 5;
const SLOT_HINTS = ["Cover", "Back", "Label", "Packaging", "In use"];

const CATEGORIES = [
  "yoga-essentials", "gir-cow", "general", "natural", "tonics",
  "churna", "oils", "tablets", "supplements",
];
const EMPTY = {
  name: "", description: "", price: 0, stock: 0, category: "general",
  brand: "", image: "", images: [], benefits: [], dosage: "", tags: [],
  owner_code: "", share_pct: "",
};

const IN_HOUSE = "in_house";

export const ProductsTab = ({ products, refresh }) => {
  const featuredCount = products.filter((p) => p.featured).length;
  const [prod, setProd] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  // Ownership is read separately: the share percentage is a private commercial
  // term and never travels in a product payload a shopper could read.
  const [owners, setOwners] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState("all");

  const loadOwners = () =>
    api.get("/admin/product-owners").then(({ data }) => {
      setOwners(Object.fromEntries((data.owners || []).map((o) => [o.product_id, o])));
      setDoctors(data.doctors || []);
    }).catch(() => {});
  useEffect(() => { loadOwners(); }, []);

  const ownerOf = (p) => owners[p.id] || { owner_code: "", owner_name: "", share_pct: 0 };
  const shown = products.filter((p) => {
    if (ownerFilter === "all") return true;
    const code = ownerOf(p).owner_code;
    return ownerFilter === IN_HOUSE ? !code : code === ownerFilter;
  });

  const migrateMedia = async () => {
    setMigrating(true);
    try {
      const { data } = await api.post("/admin/media/migrate");
      toast.success(data.moved ? `Moved ${data.moved} file(s) to media storage` : "Everything is already on media storage");
      if (data.failed) toast.error(`${data.failed} file(s) could not be moved`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setMigrating(false);
    }
  };

  const onImage = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    try {
      const room = MAX_IMAGES - (prod.images?.length || 0);
      if (room <= 0) return toast.error(`A product holds up to ${MAX_IMAGES} photos`);
      for (const f of files.slice(0, room)) {
        const { url } = await uploadFile(f, "product");
        setProd((p) => ({ ...p, images: [...(p.images || []), url] }));
      }
      if (files.length > room) toast.error(`Only ${room} more photo(s) fit — the rest were skipped`);
      else toast.success(files.length > 1 ? "Photos uploaded to storage" : "Photo uploaded to storage");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const moveImage = (from, to) => setProd((p) => {
    const next = [...(p.images || [])];
    if (to < 0 || to >= next.length) return p;
    [next[from], next[to]] = [next[to], next[from]];
    return { ...p, images: next };
  });

  const makeCover = (i) => setProd((p) => {
    const next = [...(p.images || [])];
    return { ...p, images: [next[i], ...next.filter((_, n) => n !== i)] };
  });

  const dropImage = (i) => setProd((p) => ({ ...p, images: (p.images || []).filter((_, n) => n !== i) }));

  const save = async () => {
    const share = parseFloat(prod.share_pct) || 0;
    if (prod.owner_code && !(share > 0 && share <= 100))
      return toast.error("Set a product share between 0.01% and 100% for a doctor-owned product");
    try {
      const payload = {
        ...prod,
        price: parseFloat(prod.price) || 0,
        stock: parseInt(prod.stock, 10) || 0,
        images: (prod.images || []).slice(0, MAX_IMAGES),
        image: (prod.images || [])[0] || prod.image || "",
        benefits: typeof prod.benefits === "string"
          ? prod.benefits.split(",").map((s) => s.trim()).filter(Boolean)
          : (prod.benefits || []),
        tags: Array.isArray(prod.tags) ? prod.tags : [],
      };
      delete payload.owner_code;
      delete payload.share_pct;
      let pid = editingId;
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/products", payload);
        pid = data.id;
        toast.success("Product added");
      }
      // Ownership and share live on their own endpoint, so a shopper never sees them.
      await api.patch(`/admin/products/${pid}/owner`, {
        owner_code: prod.owner_code || "",
        share_pct: prod.owner_code ? share : 0,
      });
      setProd(EMPTY); setOpen(false); setEditingId(null); refresh(); loadOwners();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const edit = (p) => {
    const owner = ownerOf(p);
    setProd({
      name: p.name || "", description: p.description || "", price: p.price || 0, stock: p.stock || 0,
      category: p.category || "general", brand: p.brand || "", image: p.image || "",
      images: (p.images && p.images.length ? p.images : (p.image ? [p.image] : [])).slice(0, MAX_IMAGES),
      benefits: p.benefits || [], dosage: p.dosage || "", tags: p.tags || [],
      owner_code: owner.owner_code || "", share_pct: owner.share_pct || "",
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    refresh();
    toast.success("Deleted");
  };

  const toggleFeatured = async (p) => {
    try {
      await api.patch(`/products/${p.id}/featured`, { featured: !p.featured });
      toast.success(p.featured ? `${p.name} removed from the homepage` : `${p.name} now shows on the homepage`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-display text-2xl">Natural Products Catalog</h3>
          <p className="text-xs text-muted-foreground mt-1" data-testid="adm-featured-count">
            {featuredCount === 0
              ? `Star up to ${FEATURED_LIMIT} products to choose the homepage carousel — it shows your newest stock until you do.`
              : `${featuredCount} of ${FEATURED_LIMIT} starred for the homepage carousel.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={migrateMedia}
            disabled={migrating}
            className="btn-pill"
            data-testid="adm-media-migrate"
            title="Move old base64 images and PDFs from the database into media storage"
          >
            <HardDriveUpload size={14} className="mr-1.5"/> {migrating ? "Moving…" : "Move Media to Storage"}
          </Button>
          <Button
            onClick={() => { setProd(EMPTY); setEditingId(null); setOpen(true); }}
            className="bg-primary text-primary-foreground btn-pill"
            data-testid="adm-prod-add"
          >
            <Plus size={14} className="mr-1"/> Add Product
          </Button>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setProd(EMPTY); setEditingId(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editingId ? "Edit Product" : "New Product"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="col-span-2"><Label>Name</Label><Input value={prod.name} onChange={(e)=>setProd({...prod,name:e.target.value})} data-testid="prod-name"/></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={prod.description} onChange={(e)=>setProd({...prod,description:e.target.value})} data-testid="prod-desc"/></div>
              <div><Label>Price (₹)</Label><Input type="number" value={prod.price} onChange={(e)=>setProd({...prod,price:e.target.value})} data-testid="prod-price"/></div>
              <div><Label>Available Quantity (Stock)</Label><Input type="number" value={prod.stock} onChange={(e)=>setProd({...prod,stock:e.target.value})} data-testid="prod-stock"/></div>
              <div>
                <Label>Category</Label>
                <Select value={prod.category} onValueChange={(v)=>setProd({...prod,category:v})}>
                  <SelectTrigger data-testid="prod-cat"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Brand (optional)</Label><Input value={prod.brand} onChange={(e)=>setProd({...prod,brand:e.target.value})} placeholder="Keshav Gir Gaushala · ગવ્ય" data-testid="prod-brand"/></div>
              <div><Label>Dosage / Usage</Label><Input value={prod.dosage} onChange={(e)=>setProd({...prod,dosage:e.target.value})} data-testid="prod-dosage"/></div>
              <div>
                <Label>Owner</Label>
                <Select
                  value={prod.owner_code || IN_HOUSE}
                  onValueChange={(v)=>setProd({...prod, owner_code: v === IN_HOUSE ? "" : v,
                                               share_pct: v === IN_HOUSE ? "" : prod.share_pct})}
                >
                  <SelectTrigger data-testid="prod-owner"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={IN_HOUSE}>In-house</SelectItem>
                    {doctors.map((d) => (
                      <SelectItem key={d.code} value={d.code}>{d.name} · {d.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {prod.owner_code && (
                <div>
                  <Label>Product share % for this doctor</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={prod.share_pct}
                    onChange={(e)=>setProd({...prod,share_pct:e.target.value})}
                    placeholder="e.g. 12.5"
                    data-testid="prod-share-pct"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Set per product — of the item total before GST and before any coupon. Earned only
                    on paid orders, and kept separate from referral credit.
                  </p>
                </div>
              )}
              <div className="col-span-2">
                <Label>Benefits (comma separated)</Label>
                <Input
                  value={Array.isArray(prod.benefits) ? prod.benefits.join(", ") : prod.benefits}
                  onChange={(e)=>setProd({...prod,benefits:e.target.value})}
                  data-testid="prod-benefits"
                />
              </div>
              <div className="col-span-2">
                <Label>Photos — up to {MAX_IMAGES} (JPEG/PNG/WebP, max 15MB each, stored on the media CDN)</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  The first photo is the cover used on the shop listing and the homepage carousel. Suggested angles:
                  {" "}{SLOT_HINTS.join(" · ")}.
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {Array.from({ length: MAX_IMAGES }).map((_, i) => {
                    const src = (prod.images || [])[i];
                    return (
                      <div
                        key={i}
                        className={`relative rounded-xl border overflow-hidden aspect-square flex items-center justify-center text-[10px] text-muted-foreground ${
                          i === 0 && src ? "border-secondary ring-1 ring-secondary/40" : "border-border border-dashed"}`}
                        data-testid={`prod-slot-${i}`}
                      >
                        {src ? (
                          <>
                            <img src={mediaUrl(src)} alt="" className="w-full h-full object-cover" />
                            {i === 0 && (
                              <span className="absolute top-1 left-1 bg-secondary text-secondary-foreground rounded-full px-1.5 text-[9px]" data-testid="prod-cover-badge">
                                Cover
                              </span>
                            )}
                            <div className="absolute bottom-0 inset-x-0 bg-background/85 flex items-center justify-center gap-0.5 py-1">
                              <button type="button" onClick={() => moveImage(i, i - 1)} disabled={i === 0} className="p-0.5 disabled:opacity-25" title="Move left" data-testid={`prod-img-left-${i}`}>
                                <ChevronLeft size={12} />
                              </button>
                              <button type="button" onClick={() => makeCover(i)} disabled={i === 0} className="p-0.5 disabled:opacity-25" title="Make this the cover" data-testid={`prod-img-cover-${i}`}>
                                <Star size={12} className={i === 0 ? "fill-current text-secondary" : ""} />
                              </button>
                              <button type="button" onClick={() => moveImage(i, i + 1)} disabled={i === (prod.images || []).length - 1} className="p-0.5 disabled:opacity-25" title="Move right" data-testid={`prod-img-right-${i}`}>
                                <ChevronRight size={12} />
                              </button>
                              <button type="button" onClick={() => dropImage(i)} className="p-0.5 text-destructive" title="Remove" data-testid={`prod-img-remove-${i}`}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </>
                        ) : (
                          <span>{SLOT_HINTS[i]}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                <Input type="file" accept="image/*" multiple onChange={onImage} className="mt-3" data-testid="prod-image"/>
                {uploading && <div className="text-xs text-muted-foreground mt-1.5" data-testid="prod-image-uploading">Uploading…</div>}
              </div>
            </div>
            <Button onClick={save} disabled={uploading} className="bg-primary text-primary-foreground mt-4" data-testid="prod-save">
              {editingId ? "Update Product" : "Save Product"}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <BulkImageTool products={products} refresh={refresh}/>

      <div className="mb-4">
        <span className="overline block mb-2">Owner</span>
        <div className="flex flex-wrap gap-2" data-testid="prod-owner-filter">
          {[["all", "All"], [IN_HOUSE, "In-house"], ...doctors.map((d) => [d.code, `${d.name} · ${d.code}`])].map(
            ([value, label]) => (
              <Button
                key={value}
                size="sm"
                variant={ownerFilter === value ? "default" : "outline"}
                className="btn-pill h-8 text-xs"
                onClick={() => setOwnerFilter(value)}
                data-testid={`prod-owner-filter-${value}`}
              >
                {label}
              </Button>
            ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="prod-owner-empty">
          No products for this owner yet.
        </p>
      ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((p) => (
          <Card key={p.id} className="border-border">
            <CardContent className="p-4">
              {p.image && <img src={mediaUrl(p.image)} alt="" className="w-full h-40 object-cover rounded-lg mb-3"/>}
              <div className="flex items-center justify-between mb-1">
                <div>
                <div className="font-display text-xl leading-tight">{p.name}</div>
                {p.brand && <div className="text-[11px] text-muted-foreground">{p.brand}</div>}
              </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleFeatured(p)}
                    className={`p-1.5 rounded hover:bg-accent ${p.featured ? "text-secondary" : "text-muted-foreground"}`}
                    data-testid={`prod-feature-${p.id}`}
                    title={p.featured ? "Remove from the homepage carousel" : "Feature on the homepage carousel"}
                  >
                    <Star size={14} fill={p.featured ? "currentColor" : "none"} />
                  </button>
                  <button onClick={()=>edit(p)} className="text-primary hover:bg-accent p-1.5 rounded" data-testid={`prod-edit-${p.id}`} title="Edit">
                    <Pencil size={14}/>
                  </button>
                  <button onClick={()=>remove(p.id)} className="text-destructive hover:bg-destructive/10 p-1.5 rounded" data-testid={`prod-del-${p.id}`} title="Delete">
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
              <div className="text-sm">₹{p.price?.toFixed(2)} · Stock: {p.stock}</div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] capitalize">{p.category}</Badge>
                {ownerOf(p).owner_code ? (
                  <Badge variant="outline" className="text-[10px] border-primary/30 text-primary bg-primary/5"
                         data-testid={`prod-owner-badge-${p.id}`}>
                    {ownerOf(p).owner_name || ownerOf(p).owner_code} · {ownerOf(p).share_pct}% share
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]" data-testid={`prod-owner-badge-${p.id}`}>
                    In-house
                  </Badge>
                )}
                {p.featured && (
                  <Badge className="text-[10px] bg-secondary text-secondary-foreground" data-testid={`prod-featured-badge-${p.id}`}>
                    On homepage
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}
    </>
  );
};

export default ProductsTab;
